import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import styles from "./styles.module.scss";

interface Option {
  value: string;
  label: string;
}

interface CustomSelectProps {
  /** A value no option has shows the placeholder. */
  value: string;
  onChange: (value: string) => void;
  options: Option[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  "aria-label"?: string;
}

// Keep in sync with .dropdown max-height: decides whether the list still
// fits under the trigger.
const MENU_MAX_HEIGHT = 280;
// Keep in sync with .dropdown max-width.
const MENU_MAX_WIDTH = 320;
const MENU_GAP = 4;

/**
 * DS single select (the client's sandbox Select): the list is portaled to
 * <body> and pinned to the trigger, so a scrolling table cell can't clip it.
 */
const CustomSelect: React.FC<CustomSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = "Select...",
  className,
  disabled = false,
  "aria-label": ariaLabel,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuBox, setMenuBox] = useState<React.CSSProperties | null>(null);
  const selectRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((opt) => opt.value === value);
  const open = isOpen && !disabled;

  const placeMenu = useCallback(() => {
    const trigger = selectRef.current?.getBoundingClientRect();
    if (!trigger) return;
    const spaceBelow = window.innerHeight - trigger.bottom;
    const openUp =
      spaceBelow < MENU_MAX_HEIGHT + MENU_GAP && trigger.top > spaceBelow;
    // Never past the window's right edge (a select on a phone's right half).
    const maxWidth = Math.max(
      trigger.width,
      Math.min(MENU_MAX_WIDTH, window.innerWidth - trigger.left - MENU_GAP * 2),
    );
    setMenuBox({
      left: Math.min(trigger.left, window.innerWidth - maxWidth - MENU_GAP * 2),
      minWidth: trigger.width,
      maxWidth,
      ...(openUp
        ? {
            bottom: window.innerHeight - trigger.top + MENU_GAP,
            maxHeight: Math.min(MENU_MAX_HEIGHT, trigger.top - MENU_GAP * 2),
          }
        : {
            top: trigger.bottom + MENU_GAP,
            maxHeight: Math.min(MENU_MAX_HEIGHT, spaceBelow - MENU_GAP * 2),
          }),
    });
  }, []);

  useEffect(() => {
    if (!open) {
      setMenuBox(null);
      return;
    }
    placeMenu();
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        selectRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      )
        return;
      setIsOpen(false);
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    // A scroll anywhere moves the trigger, so the list follows it; its own
    // scroll does not move anything.
    const handleScroll = (event: Event) => {
      if (menuRef.current?.contains(event.target as Node)) return;
      placeMenu();
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKey);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", placeMenu);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKey);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", placeMenu);
    };
  }, [open, placeMenu]);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <div
      className={`${styles.customSelect} ${className ?? ""}`}
      ref={selectRef}
    >
      <button
        type="button"
        className={`${styles.selectTrigger} ${open ? styles.open : ""}`}
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        <span
          className={`${styles.selectedValue} ${
            selectedOption ? "" : styles.placeholder
          }`}
        >
          {selectedOption?.label || placeholder}
        </span>
        <svg
          className={`${styles.arrow} ${open ? styles.arrowUp : ""}`}
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M6 8.5L10 12.5L14 8.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open &&
        menuBox &&
        createPortal(
          <div
            ref={menuRef}
            className={styles.dropdown}
            style={menuBox}
            role="listbox"
          >
            {options.map((option) => (
              <div
                key={option.value}
                className={`${styles.option} ${
                  option.value === value ? styles.selected : ""
                }`}
                onClick={() => handleSelect(option.value)}
                role="option"
                aria-selected={option.value === value}
              >
                {option.label}
              </div>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
};

export default CustomSelect;
